import dayjs from 'dayjs'
import { z } from 'zod'
import {
  contractContentSchema,
  contractDocumentStatusSchema,
  contractTemplateTypeSchema
} from '../contract/contract.ts'
import { createdAtSortQueryShape, creatorListQueryShape } from '../list-query/contract.ts'
import { accountDisplaySchema } from '../profile/contract.ts'
import { enterpriseSelectionSchema } from './enterprise-contract.ts'
import { serviceTermSchema } from './service-term-contract.ts'

export * from './service-term-contract.ts'
export * from './enterprise-contract.ts'
export { fillRentalContract } from './rental-contract-content.ts'

export const businessSubjectKindSchema = z.enum(['person', 'prospectiveCompany', 'registeredCompany'])
export const contractPartyKindSchema = z.enum(['customer', 'businessSubject', 'identity'])
export const rentalOrderStatusSchema = z.enum([
  'pending',
  'awaitingBilling',
  'active',
  'ended',
  'cancelled',
  'archived'
])
export const serviceOrderStatusSchema = z.enum([
  'pending',
  'active',
  'ended',
  'terminated',
  'completed',
  'cancelled',
  'archived'
])
// archived 仅供历史订单 DTO 兼容，业务状态筛选与归档筛选分别表达。
export const rentalOrderBusinessStatusSchema = rentalOrderStatusSchema.exclude(['archived'])
export const serviceOrderBusinessStatusSchema = serviceOrderStatusSchema.exclude(['archived'])
export const orderArchiveStatusSchema = z.enum(['unarchived', 'archived', 'all'])
export const occupancyModeSchema = z.enum(['physical', 'registration'])
export const leaseActionSchema = z.enum(['setup', 'change', 'registration'])
export const rentTypeSchema = z.enum(['daily', 'monthly', 'yearly'])

const moneySchema = z.number().finite().min(0).max(9_999_999_999.99).multipleOf(0.01, '金额最多保留两位小数')
const positiveIntegerSchema = z.number().int().positive()
const positiveIntegerFilterSchema = z.union([positiveIntegerSchema, z.array(positiveIntegerSchema).min(1).max(100)])
const orderGroupNameSchema = z.string().trim().max(255).optional().nullable()
const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式应为 YYYY-MM-DD')
const dateTimeStringSchema = z.string().refine((value) => dayjs(value).isValid(), '日期时间格式不正确')
const jsonObjectSchema = z.record(z.string(), z.unknown())

export const orderGroupCreateSchema = z.object({
  customerId: positiveIntegerSchema,
  name: orderGroupNameSchema
})

export const orderGroupListSchema = z.object({
  current: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  id: positiveIntegerSchema.optional(),
  keyword: z.string().trim().max(255).optional(),
  customerId: positiveIntegerFilterSchema.optional(),
  customerKeyword: z.string().trim().max(255).optional(),
  creatorKeyword: z.string().trim().max(255).optional(),
  createdFrom: dateTimeStringSchema.optional(),
  createdTo: dateTimeStringSchema.optional(),
  sortField: z.enum(['createdAt']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional()
})

export const orderGroupOptionsSchema = z.object({ customerId: positiveIntegerSchema.optional() })

export const orderGroupUpdateSchema = z.object({
  id: positiveIntegerSchema,
  name: z.string().trim().min(1, '订单组名称不能为空').max(255)
})

export const orderGroupSchema = z.object({
  id: positiveIntegerSchema,
  name: z.string(),
  customerId: positiveIntegerSchema,
  customerName: z.string(),
  rentalOrderCount: z.number().int().nonnegative(),
  serviceOrderCount: z.number().int().nonnegative(),
  orderCount: z.number().int().nonnegative(),
  createdAt: z.date(),
  updatedAt: z.date()
})

export const orderGroupListItemSchema = orderGroupSchema.extend({ creator: accountDisplaySchema })
export const orderGroupListOutputSchema = z.object({
  data: z.array(orderGroupListItemSchema),
  total: z.number().int().nonnegative(),
  current: z.number().int().positive(),
  pageSize: z.number().int().positive()
})

export const orderGroupSelectionSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('new'), name: orderGroupNameSchema }),
  z.object({ mode: z.literal('existing'), id: positiveIntegerSchema })
])

export const businessSubjectSchema = z.object({
  id: positiveIntegerSchema,
  customerId: positiveIntegerSchema,
  kind: businessSubjectKindSchema,
  displayName: z.string(),
  companyInfoId: positiveIntegerSchema.nullable(),
  primaryIdentityId: positiveIntegerSchema.nullable(),
  note: z.string().nullable(),
  isArchived: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date()
})

export const businessSubjectCreateSchema = z.object({
  customerId: positiveIntegerSchema,
  kind: businessSubjectKindSchema,
  displayName: z.string().trim().min(1).max(255),
  companyInfoId: positiveIntegerSchema.optional().nullable(),
  primaryIdentityId: positiveIntegerSchema.optional().nullable(),
  note: z.string().trim().max(5000).optional().nullable()
})

export const businessSubjectListSchema = z.object({
  ...creatorListQueryShape,
  ...createdAtSortQueryShape,
  current: z.number().int().positive().default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  id: positiveIntegerSchema.optional(),
  keyword: z.string().trim().max(255).optional(),
  customerId: positiveIntegerSchema.optional(),
  kind: businessSubjectKindSchema.optional(),
  isArchived: z.boolean().optional()
})
export const businessSubjectListOutputSchema = z.object({
  data: businessSubjectSchema.extend({ customerName: z.string(), creator: accountDisplaySchema }).array(),
  total: z.number().int().nonnegative(),
  current: positiveIntegerSchema,
  pageSize: positiveIntegerSchema
})
export type BusinessSubjectListInput = z.infer<typeof businessSubjectListSchema>

const attachmentSchema = z.object({
  id: positiveIntegerSchema,
  file: z.object({
    id: positiveIntegerSchema,
    fileName: z.string(),
    originName: z.string(),
    fileSize: z.string(),
    fileType: z.string().nullable()
  })
})

export const orderContractSettingsSchema = z
  .array(
    z.object({
      contractType: contractTemplateTypeSchema,
      templateId: positiveIntegerSchema.optional()
    })
  )
  .max(20)
export type OrderContractSettings = z.infer<typeof orderContractSettingsSchema>

export const serviceOrderSchema = z.object({
  initialBillStatus: z.enum(['draft', 'issued', 'voided']).nullable(),
  serviceMode: z.enum(['once', 'term']).default('once'),
  term: serviceTermSchema.nullish(),
  startAt: z.date().nullish(),
  endAt: z.date().nullish(),
  terminatedAt: z.date().nullish(),
  renewedFromId: positiveIntegerSchema.nullish(),
  contractSettings: orderContractSettingsSchema.optional(),
  contractDocumentCount: z.number().int().nonnegative().default(0),
  creator: accountDisplaySchema,
  id: positiveIntegerSchema,
  orderNo: z.string(),
  type: z.literal('service'),
  customerId: positiveIntegerSchema,
  customerName: z.string(),
  orderGroupId: positiveIntegerSchema.nullable(),
  orderGroupName: z.string().nullable(),
  subjectId: positiveIntegerSchema.nullable(),
  subjectName: z.string().nullable(),
  subjectKind: businessSubjectKindSchema.nullable(),
  companyName: z.string().nullable(),
  identityName: z.string().nullable(),
  salespersonId: positiveIntegerSchema.nullable(),
  salespersonName: z.string(),
  businessId: positiveIntegerSchema,
  businessName: z.string(),
  businessColor: z.string().nullable().optional(),
  organizationId: positiveIntegerSchema,
  organizationName: z.string(),
  referencePrice: z.string().nullable(),
  agreedAmount: z.string().nullable(),
  totalAmount: z.string().nullable(),
  costAmount: z.string().nullable(),
  billedAmount: z.string(),
  receivedAmount: z.string(),
  refundedAmount: z.string(),
  outstandingAmount: z.string(),
  refundableAmount: z.string(),
  status: serviceOrderStatusSchema,
  editRestriction: z.string().nullable(),
  detail: z.unknown().nullable(),
  subjectSnapshot: z.unknown().nullable(),
  note: z.string().nullable(),
  createAccountId: positiveIntegerSchema,
  attachments: z.array(attachmentSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
  archivedAt: z.date().nullable()
})

const rentalSubjectSnapshotSchema = z.object({
  subject: z
    .object({
      id: positiveIntegerSchema,
      kind: businessSubjectKindSchema,
      displayName: z.string()
    })
    .nullable(),
  company: z.object({ id: positiveIntegerSchema, name: z.string() }).nullable(),
  identity: z.object({ id: positiveIntegerSchema, name: z.string() }).nullable()
})

export const rentalOrderSchema = z.object({
  contractSettings: orderContractSettingsSchema.optional(),
  contractDocumentCount: z.number().int().nonnegative().default(0),
  // 未出具合同的待处理订单，可补关联或更换服务对象及资料。
  canAssociateEnterprise: z.boolean().default(false),
  missingContractData: z.array(z.string()),
  creator: accountDisplaySchema,
  id: positiveIntegerSchema,
  orderNo: z.string(),
  customerId: positiveIntegerSchema,
  customerName: z.string(),
  orderGroupId: positiveIntegerSchema.nullable(),
  orderGroupName: z.string().nullable(),
  subjectId: positiveIntegerSchema.nullable(),
  subjectName: z.string().nullable(),
  companyInfoId: positiveIntegerSchema.nullable(),
  companyName: z.string().nullable(),
  identityInfoId: positiveIntegerSchema.nullable(),
  identityName: z.string().nullable(),
  salespersonId: positiveIntegerSchema.nullable(),
  salespersonName: z.string(),
  businessId: positiveIntegerSchema.nullable(),
  businessName: z.string().nullable(),
  businessColor: z.string().nullable().optional(),
  organizationId: positiveIntegerSchema,
  organizationName: z.string(),
  siteId: positiveIntegerSchema,
  siteName: z.string(),
  roomId: positiveIntegerSchema.nullable(),
  roomNo: z.string().nullable(),
  leaseAction: leaseActionSchema,
  occupancyMode: occupancyModeSchema,
  rentType: rentTypeSchema,
  billingInterval: z.number().int().positive(),
  startAt: z.date(),
  endAt: z.date(),
  terminatedAt: z.date().nullable(),
  renewedFromId: positiveIntegerSchema.nullable(),
  referencePrice: z.string().nullable(),
  agreedPrice: z.string(),
  managementFee: z.string().nullable(),
  waterUnitPrice: z.string().nullable(),
  electricityUnitPrice: z.string().nullable(),
  pricingTerms: z.unknown().nullable(),
  depositAmount: z.string().nullable(),
  billedAmount: z.string(),
  receivedAmount: z.string(),
  refundedAmount: z.string(),
  outstandingAmount: z.string(),
  refundableAmount: z.string(),
  status: rentalOrderStatusSchema,
  contractPartyKind: contractPartyKindSchema.nullable(),
  contractPartyName: z.string().nullable(),
  contractType: contractTemplateTypeSchema.nullable(),
  contractTemplateId: positiveIntegerSchema.nullable(),
  contractTemplateName: z.string().nullable(),
  subjectSnapshot: rentalSubjectSnapshotSchema.nullable(),
  addressSnapshot: z.unknown(),
  note: z.string().nullable(),
  createAccountId: positiveIntegerSchema,
  attachments: z.array(attachmentSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
  archivedAt: z.date().nullable()
})

export const orderGroupDetailSchema = orderGroupSchema.extend({
  rentalOrders: z.array(rentalOrderSchema),
  serviceOrders: z.array(serviceOrderSchema)
})

export const serviceOrderListSchema = z.object({
  serviceMode: z.enum(['once', 'term']).optional(),
  ...creatorListQueryShape,
  ...createdAtSortQueryShape,
  subjectId: positiveIntegerFilterSchema.optional(),
  current: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  id: positiveIntegerSchema.optional(),
  keyword: z.string().trim().max(255).optional(),
  note: z.string().trim().max(255).optional(),
  status: z
    .union([serviceOrderBusinessStatusSchema, z.array(serviceOrderBusinessStatusSchema).min(1).max(20)])
    .optional(),
  archiveStatus: orderArchiveStatusSchema.optional(),
  businessId: positiveIntegerFilterSchema.optional(),
  customerId: positiveIntegerFilterSchema.optional(),
  salespersonId: positiveIntegerFilterSchema.optional(),
  orderGroupId: positiveIntegerSchema.optional()
})

export const rentalOrderListSchema = z.object({
  ...creatorListQueryShape,
  ...createdAtSortQueryShape,
  subjectId: positiveIntegerFilterSchema.optional(),
  subjectAssociation: z.enum(['linked', 'unlinked']).optional(),
  current: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  id: positiveIntegerSchema.optional(),
  keyword: z.string().trim().max(255).optional(),
  note: z.string().trim().max(255).optional(),
  status: z
    .union([rentalOrderBusinessStatusSchema, z.array(rentalOrderBusinessStatusSchema).min(1).max(20)])
    .optional(),
  archiveStatus: orderArchiveStatusSchema.optional(),
  businessId: positiveIntegerFilterSchema.optional(),
  customerId: positiveIntegerFilterSchema.optional(),
  salespersonId: positiveIntegerFilterSchema.optional(),
  orderGroupId: positiveIntegerSchema.optional(),
  siteId: positiveIntegerFilterSchema.optional(),
  roomId: positiveIntegerSchema.optional()
})

export const serviceOrderListOutputSchema = z.object({
  data: z.array(serviceOrderSchema),
  total: z.number().int().nonnegative(),
  current: z.number().int().positive(),
  pageSize: z.number().int().positive()
})

export const rentalOrderListOutputSchema = z.object({
  data: z.array(rentalOrderSchema),
  total: z.number().int().nonnegative(),
  current: z.number().int().positive(),
  pageSize: z.number().int().positive()
})

const serviceOrderFieldsSchema = z.object({
  term: serviceTermSchema.nullish(),
  contractSettings: orderContractSettingsSchema.optional(),
  businessId: positiveIntegerSchema,
  enterpriseSelection: enterpriseSelectionSchema.optional(),
  subjectId: positiveIntegerSchema.optional().nullable(),
  costAmount: moneySchema.optional().nullable(),
  fileIds: z.array(positiveIntegerSchema).max(20).optional(),
  agreedAmount: moneySchema.nullish(),
  detail: jsonObjectSchema.optional().nullable(),
  note: z.string().trim().max(5000).optional().nullable()
})

function validServiceAmount(value: { term?: unknown; agreedAmount?: number | null }) {
  return Boolean(value.term) || value.agreedAmount != null
}

const serviceOrderItemSchema = serviceOrderFieldsSchema
  .extend({
    status: z.literal('pending').default('pending')
  })
  .refine(validServiceAmount, { message: '一次性服务必须填写应收金额', path: ['agreedAmount'] })
  .refine((value) => !value.enterpriseSelection || value.subjectId === undefined, {
    message: '服务对象选用与旧办理对象字段不能同时提交',
    path: ['enterpriseSelection']
  })

export const serviceOrderCreateSchema = z.object({
  customerId: positiveIntegerSchema,
  organizationId: positiveIntegerSchema,
  salespersonId: positiveIntegerSchema.nullish(),
  orderGroup: orderGroupSelectionSchema.optional(),
  idempotencyKey: z.uuid(),
  items: z.array(serviceOrderItemSchema).min(1).max(100)
})

export const serviceOrderDraftUpdateSchema = serviceOrderFieldsSchema
  .extend({
    idempotencyKey: z.uuid().optional(),
    serviceOrderId: positiveIntegerSchema.optional(),
    // 兼容当前页面参数；新接口应使用 serviceOrderId。
    orderId: positiveIntegerSchema.optional(),
    customerId: positiveIntegerSchema,
    salespersonId: positiveIntegerSchema.nullish(),
    orderGroupId: positiveIntegerSchema.optional().nullable()
  })
  .transform(({ orderId, serviceOrderId, ...data }) => ({
    ...data,
    serviceOrderId: serviceOrderId ?? orderId
  }))
  .refine((value) => value.serviceOrderId !== undefined, '服务订单 ID 不能为空')
  .refine(validServiceAmount, { message: '一次性服务必须填写应收金额', path: ['agreedAmount'] })
  .refine((value) => !value.enterpriseSelection || value.subjectId === undefined, {
    message: '服务对象选用与旧办理对象字段不能同时提交',
    path: ['enterpriseSelection']
  })
  .refine((value) => !value.enterpriseSelection || Boolean(value.idempotencyKey), {
    message: '编辑服务对象资料必须提供幂等提交凭据',
    path: ['idempotencyKey']
  })

const rentalOrderFieldsSchema = z.object({
  enterpriseSelection: enterpriseSelectionSchema.optional(),
  reuseCompanyInfo: z.boolean().optional(),
  reuseIdentityInfo: z.boolean().optional(),
  customerId: positiveIntegerSchema,
  salespersonId: positiveIntegerSchema.nullish(),
  orderGroup: orderGroupSelectionSchema.optional(),
  subjectId: positiveIntegerSchema.optional().nullable(),
  companyInfoId: positiveIntegerSchema.optional().nullable(),
  identityInfoId: positiveIntegerSchema.optional().nullable(),
  businessId: positiveIntegerSchema.optional().nullable(),
  siteId: positiveIntegerSchema,
  roomId: positiveIntegerSchema.optional().nullable(),
  leaseAction: leaseActionSchema,
  occupancyMode: occupancyModeSchema,
  rentType: rentTypeSchema,
  billingInterval: z.number().int().positive().default(1),
  startAt: dateSchema,
  endAt: dateSchema,
  referencePrice: moneySchema.optional().nullable(),
  agreedPrice: moneySchema,
  managementFee: moneySchema.optional().nullable(),
  waterUnitPrice: moneySchema.optional().nullable(),
  electricityUnitPrice: moneySchema.optional().nullable(),
  pricingTerms: jsonObjectSchema.optional().nullable(),
  depositAmount: moneySchema.nullish(),
  contractSettings: orderContractSettingsSchema.optional(),
  contractPartyKind: contractPartyKindSchema.optional().nullable(),
  contractPartyName: z.string().trim().max(255).optional().nullable(),
  contractType: contractTemplateTypeSchema.optional().nullable(),
  contractTemplateId: positiveIntegerSchema.optional().nullable(),
  addressSnapshot: jsonObjectSchema,
  fileIds: z.array(positiveIntegerSchema).max(20).optional(),
  note: z.string().trim().max(5000).optional().nullable(),
  idempotencyKey: z.uuid().optional(),
  services: z.array(serviceOrderItemSchema).max(100).optional()
})

function hasSingleEnterpriseInput(value: {
  enterpriseSelection?: unknown
  subjectId?: number | null
  companyInfoId?: number | null
  identityInfoId?: number | null
  reuseCompanyInfo?: boolean
  reuseIdentityInfo?: boolean
}) {
  return (
    !value.enterpriseSelection ||
    [value.subjectId, value.companyInfoId, value.identityInfoId, value.reuseCompanyInfo, value.reuseIdentityInfo].every(
      (field) => field === undefined
    )
  )
}
export const rentalOrderCreateSchema = rentalOrderFieldsSchema
  .extend({ renewedFromId: positiveIntegerSchema.optional() })
  .refine(hasSingleEnterpriseInput, {
    message: '服务对象选用与旧执照、法人字段不能同时提交',
    path: ['enterpriseSelection']
  })
  .refine(
    (value) =>
      !(value.enterpriseSelection || value.services?.some((item) => item.enterpriseSelection)) ||
      Boolean(value.idempotencyKey),
    {
      message: '选用服务对象的新建订单必须提供幂等提交凭据',
      path: ['idempotencyKey']
    }
  )

// 批量建单只接收共享条款；房间与地址快照由服务端分配。
export const rentalOrderBatchCreateSchema = rentalOrderFieldsSchema
  .omit({
    enterpriseSelection: true,
    reuseCompanyInfo: true,
    reuseIdentityInfo: true,
    subjectId: true,
    companyInfoId: true,
    identityInfoId: true,
    roomId: true,
    addressSnapshot: true,
    fileIds: true,
    services: true
  })
  .extend({
    quantity: z.number().int().min(1).max(100),
    businessId: positiveIntegerSchema,
    idempotencyKey: z.uuid()
  })
  .refine((value) => value.endAt > value.startAt, {
    message: '租赁结束日期必须晚于开始日期',
    path: ['endAt']
  })
export const rentalOrderBatchCreateOutputSchema = z.object({
  orders: z.array(z.object({ id: positiveIntegerSchema, orderNo: z.string() }))
})
export type RentalOrderBatchCreateInput = z.infer<typeof rentalOrderBatchCreateSchema>

export const rentalOrderDraftUpdateSchema = rentalOrderFieldsSchema
  .omit({ services: true })
  .extend({
    rentalOrderId: positiveIntegerSchema
  })
  .refine(hasSingleEnterpriseInput, {
    message: '服务对象选用与旧执照、法人字段不能同时提交',
    path: ['enterpriseSelection']
  })
  .refine((value) => !value.enterpriseSelection || Boolean(value.idempotencyKey), {
    message: '编辑服务对象资料必须提供幂等提交凭据',
    path: ['idempotencyKey']
  })
export const orderNotesUpdateSchema = z.object({
  orderId: positiveIntegerSchema,
  note: z.string().trim().max(5000).nullable(),
  fileIds: z.array(positiveIntegerSchema).max(20)
})
export type RentalOrderDraftUpdateInput = z.infer<typeof rentalOrderDraftUpdateSchema>
export type OrderNotesUpdateInput = z.infer<typeof orderNotesUpdateSchema>

export const orderBatchRemoveSchema = z.object({ ids: z.array(positiveIntegerSchema).min(1).max(100) })
export const serviceOrderSearchBatchSchema = z.object({
  action: z.enum(['delete', 'forceDelete']),
  search: serviceOrderListSchema,
  expectedCount: z.number().int().positive(),
  confirmation: z.string().trim().optional()
})
export const rentalOrderSearchBatchSchema = serviceOrderSearchBatchSchema.extend({ search: rentalOrderListSchema })
export const rentalContractBySearchOutputSchema = z.object({ ids: z.array(positiveIntegerSchema).min(1).max(100) })
export const ORDER_SEARCH_BATCH_ASYNC_THRESHOLD = 200

export const orderSearchBatchOutputSchema = z.object({
  count: z.number().int().nonnegative(),
  queued: z.boolean(),
  jobId: z.string().optional(),
  successCount: z.number().int().nonnegative().optional(),
  failedCount: z.number().int().nonnegative().optional(),
  warnings: z.array(z.string()).default([])
})
export const orderSearchBatchStatusInputSchema = z.object({ jobId: z.string().min(1) })
export const orderSearchBatchStatusOutputSchema = z.object({
  state: z.enum(['waiting', 'active', 'completed', 'failed', 'missing']),
  processed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  successCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  warnings: z.array(z.string()).default([])
})
export const orderBatchRemoveOutputSchema = z.object({
  successCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  failures: z.array(z.object({ id: positiveIntegerSchema, message: z.string() }))
})

export const serviceOrderIdSchema = z.object({ serviceOrderId: positiveIntegerSchema })
export const rentalOrderArchiveSchema = z.object({
  rentalOrderId: positiveIntegerSchema,
  reason: z.string().trim().min(1, '请填写归档原因').max(5000)
})
export const rentalOrderIdSchema = z.object({ rentalOrderId: positiveIntegerSchema })
export const orderGroupBatchDeleteSchema = z.object({
  ids: z.array(positiveIntegerSchema).min(1, '至少选择一个订单组').max(100, '单次最多删除 100 个订单组')
})
export const orderGroupBatchDeleteOutputSchema = z.object({ count: z.number().int().nonnegative() })

export const orderGroupIdSchema = z.object({ orderGroupId: positiveIntegerSchema })

export const serviceOrderBatchOutputSchema = serviceOrderSchema.array()
export const orderRemoveOutputSchema = z.object({ id: positiveIntegerSchema })

export type BusinessSubject = z.infer<typeof businessSubjectSchema>
export type OrderGroup = z.infer<typeof orderGroupSchema>
export type OrderGroupListItem = z.infer<typeof orderGroupListItemSchema>
export type OrderGroupCreateInput = z.infer<typeof orderGroupCreateSchema>
export type OrderGroupListInput = z.infer<typeof orderGroupListSchema>
export type OrderGroupOptionsInput = z.infer<typeof orderGroupOptionsSchema>
export type OrderGroupUpdateInput = z.infer<typeof orderGroupUpdateSchema>
export type OrderGroupSelectionInput = z.infer<typeof orderGroupSelectionSchema>
export type BusinessSubjectCreateInput = z.infer<typeof businessSubjectCreateSchema>
export type ServiceOrder = z.infer<typeof serviceOrderSchema>
export type RentalOrder = z.infer<typeof rentalOrderSchema>
export type ServiceOrderCreateInput = z.infer<typeof serviceOrderCreateSchema>
export type ServiceOrderItemInput = z.infer<typeof serviceOrderItemSchema>
export type ServiceOrderDraftUpdateInput = z.infer<typeof serviceOrderDraftUpdateSchema>
export type ServiceOrderListInput = z.infer<typeof serviceOrderListSchema>
export type RentalOrderCreateInput = z.infer<typeof rentalOrderCreateSchema>
export type RentalOrderListInput = z.infer<typeof rentalOrderListSchema>

export const rentalContractPrepareSchema = z.object({
  rentalOrderId: positiveIntegerSchema,
  templateId: positiveIntegerSchema,
  partyName: z.string().trim().max(255).optional()
})
export const rentalContractSaveSchema = z
  .object({
    rentalOrderId: positiveIntegerSchema,
    partyName: z.string().trim().max(255).optional(),
    sourceDocumentId: positiveIntegerSchema.optional(),
    templateId: positiveIntegerSchema.optional(),
    title: z.string().trim().min(1).max(255),
    templateVersionId: positiveIntegerSchema.optional(),
    expectedUpdatedAt: z.date(),
    content: contractContentSchema
  })
  .refine(
    (value) => Boolean(value.sourceDocumentId || (value.templateId && value.templateVersionId)),
    '请选择合同副本或模板'
  )
export type RentalContractPrepareInput = z.infer<typeof rentalContractPrepareSchema>
export type RentalContractSaveInput = z.infer<typeof rentalContractSaveSchema>
export const rentalContractBatchSaveSchema = z
  .object({ items: z.array(rentalContractSaveSchema).min(1).max(2000) })
  .superRefine(({ items }, ctx) => {
    const groups = new Map<number, typeof items>()
    for (const item of items) {
      groups.set(item.rentalOrderId, [...(groups.get(item.rentalOrderId) ?? []), item])
    }
    if (groups.size > 100) {
      ctx.addIssue({ code: 'custom', message: '一次最多选择 100 个订单', path: ['items'] })
    }
    for (const contracts of groups.values()) {
      if (contracts.length > 20) {
        ctx.addIssue({ code: 'custom', message: '每个订单最多保存 20 份合同', path: ['items'] })
      }
      if (contracts.some((item) => !dayjs(item.expectedUpdatedAt).isSame(contracts[0].expectedUpdatedAt))) {
        ctx.addIssue({ code: 'custom', message: '同一订单的合同版本不一致，请重新载入', path: ['items'] })
      }
      const sources = contracts.flatMap((item) => (item.sourceDocumentId ? [item.sourceDocumentId] : []))
      if (new Set(sources).size !== sources.length) {
        ctx.addIssue({ code: 'custom', message: '不能重复修改同一份合同', path: ['items'] })
      }
    }
  })
export const orderContractKeySchema = z.object({
  orderType: z.enum(['rental', 'service']),
  orderId: z.number().int().positive()
})
export const orderContractPrepareSchema = orderContractKeySchema.extend({
  templateId: z.number().int().positive(),
  partyName: z.string().trim().max(255).optional()
})
export const orderContractsSaveSchema = rentalContractBatchSaveSchema.and(
  z.object({ orderType: z.enum(['rental', 'service']) })
)

export type RentalContractBatchSaveInput = z.infer<typeof rentalContractBatchSaveSchema>

/** 订单可缺资料，合同必须使用已关联且完整的档案。只返回缺失项，不暴露证件号码。 */
export function getRentalContractMissingData(order: {
  CompanyInfo?: {
    name: string
    unifiedSocialCreditCode?: string | null
    registeredCapital?: string | null
    legalRepresentativeIdentityId?: number | null
  } | null
  IdentityInfo?: { id?: number; name: string; idNumber?: string | null } | null
}) {
  const missing: string[] = []
  if (order.CompanyInfo) {
    if (!order.CompanyInfo.name.trim()) {
      missing.push('企业名称')
    }
    if (!/^[0-9A-Z]{18}$/.test(order.CompanyInfo.unifiedSocialCreditCode ?? '')) {
      missing.push('统一社会信用代码')
    }
    if (!order.CompanyInfo.registeredCapital?.trim()) {
      missing.push('注册资本')
    }
    if (!order.CompanyInfo.legalRepresentativeIdentityId) {
      missing.push('企业与法人的关联')
    } else if (order.CompanyInfo.legalRepresentativeIdentityId !== order.IdentityInfo?.id) {
      missing.push('与企业档案一致的法人')
    }
  } else if (!order.IdentityInfo) {
    missing.push('执照资料或法人资料')
  }
  if (!order.IdentityInfo) {
    if (order.CompanyInfo) {
      missing.push('法人资料')
    }
  } else {
    if (!order.IdentityInfo.name.trim()) {
      missing.push('法人姓名')
    }
    if (!/^(?:[0-9]{15}|[0-9]{17}[0-9X])$/.test(order.IdentityInfo.idNumber ?? '')) {
      missing.push('身份证号码')
    }
  }
  return missing
}

export const rentalOrderForceRemoveSchema = z.object({
  rentalOrderId: positiveIntegerSchema,
  confirmation: z.string().trim().min(1).max(64)
})

export const rentalOrderBatchForceRemoveSchema = z
  .object({
    orders: z
      .array(rentalOrderForceRemoveSchema)
      .min(1)
      .max(100)
      .refine((orders) => new Set(orders.map((order) => order.rentalOrderId)).size === orders.length, '订单不能重复'),
    confirmation: z.string().trim()
  })
  .refine((input) => input.confirmation === `强制删除 ${input.orders.length} 个订单`, '请输入完整的批量删除确认文字')
export type RentalOrderBatchForceRemoveInput = z.infer<typeof rentalOrderBatchForceRemoveSchema>

export const serviceOrderForceRemoveSchema = z.object({
  serviceOrderId: positiveIntegerSchema,
  confirmation: z.string().trim().min(1).max(64)
})
export const serviceOrderBatchForceRemoveSchema = z
  .object({
    orders: z
      .array(serviceOrderForceRemoveSchema)
      .min(1)
      .max(100)
      .refine((orders) => new Set(orders.map((order) => order.serviceOrderId)).size === orders.length, '订单不能重复'),
    confirmation: z.string().trim()
  })
  .refine((input) => input.confirmation === `强制删除 ${input.orders.length} 个订单`, '请输入完整的批量删除确认文字')
export type ServiceOrderBatchForceRemoveInput = z.infer<typeof serviceOrderBatchForceRemoveSchema>
export const orderForceRemoveOutputSchema = z.object({ id: positiveIntegerSchema, warnings: z.array(z.string()) })
export const orderBatchForceRemoveOutputSchema = orderBatchRemoveOutputSchema.extend({ warnings: z.array(z.string()) })

export const orderBatchUpdateSchema = z.object({
  ids: z.array(positiveIntegerSchema).min(1).max(100),
  idempotencyKey: z.uuid(),
  change: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('salesperson'), salespersonId: positiveIntegerSchema.nullable() }),
    z.object({ kind: z.literal('customer'), customerId: positiveIntegerSchema }),
    z.object({ kind: z.literal('group'), orderGroup: orderGroupSelectionSchema })
  ])
})
export const orderBatchUpdateOutputSchema = z.object({
  count: z.number().int().nonnegative(),
  queued: z.boolean().optional(),
  jobId: z.string().optional()
})
export type OrderBatchUpdateInput = z.infer<typeof orderBatchUpdateSchema>

export const serviceOrderBatchUpdateBySearchSchema = orderBatchUpdateSchema.omit({ ids: true }).extend({
  search: serviceOrderListSchema,
  expectedCount: positiveIntegerSchema
})
export const rentalOrderBatchUpdateBySearchSchema = serviceOrderBatchUpdateBySearchSchema.extend({
  search: rentalOrderListSchema
})
export type OrderBatchUpdateBySearchInput =
  z.infer<typeof rentalOrderBatchUpdateBySearchSchema> | z.infer<typeof serviceOrderBatchUpdateBySearchSchema>
export const orderBatchSelectionOutputSchema = z.object({
  total: z.number().int().nonnegative(),
  commonCustomerId: positiveIntegerSchema.nullable()
})

export const rentalEndPreviewSchema = z.object({ rentalOrderId: positiveIntegerSchema, date: dateSchema })
export const rentalEndSchema = rentalEndPreviewSchema.extend({
  reason: z.string().trim().min(1).max(1000),
  expectedUpdatedAt: z.date()
})
export const rentalRenewSchema = z.object({
  rentalOrderId: positiveIntegerSchema,
  endAt: dateSchema,
  agreedPrice: z.string().regex(/^(0|[1-9]\d{0,9})(\.\d{1,2})?$/),
  depositAmount: z
    .string()
    .regex(/^(0|[1-9]\d{0,9})(\.\d{1,2})?$/)
    .nullish(),
  idempotencyKey: z.uuid()
})

export const rentalInitialBillsOutputSchema = z.object({
  id: z.number().int().positive(),
  bills: z.array(z.object({ id: z.number().int().positive(), feeType: z.string(), requiredForActivation: z.boolean() }))
})

const orderContractIdsTargetSchema = z.object({
  mode: z.literal('ids'),
  ids: z.array(positiveIntegerSchema).min(1).max(100)
})
export const orderContractBatchSetStatusSchema = z.discriminatedUnion('orderType', [
  z.object({
    orderType: z.literal('rental'),
    status: contractDocumentStatusSchema,
    documentIds: z.array(positiveIntegerSchema).min(1).max(100).optional(),
    target: z.discriminatedUnion('mode', [
      orderContractIdsTargetSchema,
      z.object({
        mode: z.literal('search'),
        search: rentalOrderListSchema,
        expectedCount: z.number().int().nonnegative()
      })
    ])
  }),
  z.object({
    orderType: z.literal('service'),
    status: contractDocumentStatusSchema,
    documentIds: z.array(positiveIntegerSchema).min(1).max(100).optional(),
    target: z.discriminatedUnion('mode', [
      orderContractIdsTargetSchema,
      z.object({
        mode: z.literal('search'),
        search: serviceOrderListSchema,
        expectedCount: z.number().int().nonnegative()
      })
    ])
  })
])
export type OrderContractBatchSetStatusInput = z.infer<typeof orderContractBatchSetStatusSchema>

// 订单组操作始终在服务端覆盖组内全部订单，与详情页当前筛选和分页无关。
export const orderGroupManageSchema = z.object({
  orderGroupId: positiveIntegerSchema,
  expectedCustomerId: positiveIntegerSchema,
  expectedCount: z.number().int().nonnegative(),
  idempotencyKey: z.uuid(),
  change: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('customer'), customerId: positiveIntegerSchema }),
    z.object({ kind: z.literal('salesperson'), salespersonId: positiveIntegerSchema.nullable() }),
    z.object({ kind: z.literal('contractStatus'), status: contractDocumentStatusSchema }),
    z
      .object({
        kind: z.literal('include'),
        rentalIds: z.array(positiveIntegerSchema).max(100),
        serviceIds: z.array(positiveIntegerSchema).max(100)
      })
      .refine((value) => value.rentalIds.length + value.serviceIds.length > 0, '请选择订单')
  ])
})
export type OrderGroupManageInput = z.infer<typeof orderGroupManageSchema>
